/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import {
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  ToggleButtonGroup,
  ToggleButton,
  Grid,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { CustomStack, CustomToggleSwitch } from 'modules/common/components';
import InfoIcon from '@mui/icons-material/Info';
import styles from 'modules/dashboard/components/tab-container/inner-filter/inner-filter.module.scss';
import { selectForecastDisabledText, selectsHotelForecast } from '../../../../../selectors';
/**
 * Booking pace window component common for the dashboard component
 * @param {*} paceHorizon - Pace horizon selected state
 * @param {Array} paceList - Pace values list
 * @param {*} paceVariable - Pace variable selected state
 * @param {Function} handlePaceHorizonChange - Function to set pace horizon value
 * @param {Function} handlePaceVariableChange - Function to set pace variable value
 * @param {Boolean} isPlot - check 3d plot tab
 * @param {Array} paceVariableList - Pace variable list
 * @param {Boolean} visualToggle - is Far Field visual shown
 * @param handleForecastFilter
 * @param enableForecastFilter
 * @returns
 */
const BookingPaceWindow = ({
  paceHorizon,
  paceList,
  paceVariable,
  handlePaceHorizonChange,
  handlePaceVariableChange,
  isPlot,
  paceVariableList = [],
  visualToggle,
  handleForecastFilterChange,
  enableForecastFilter,
  summaryForecastFilter,
  showFilter,
}) => {
  const isHotelForecast = useSelector(selectsHotelForecast);
  const ForecastDisabledText = useSelector(selectForecastDisabledText);
  //
  return (
    <CustomStack cssClassName={styles.innerFilter}>
      {isPlot && (
        <FormControl className='pace-variable'>
          <FormLabel id="breakdown-label">Booking Pace Parameter</FormLabel>
          <RadioGroup
            value={paceVariable}
            name="radio-buttons-group"
            onChange={handlePaceVariableChange}
          >
            <FormControlLabel
              sx={{
                my: -1,
              }}
              value={false}
              control={<Radio />}
              label="Revenue"
            />
            <FormControlLabel
              sx={{
                my: -1,
              }}
              value
              control={<Radio />}
              label="Occupied Rooms"
            />
          </RadioGroup>
        </FormControl>
      )}
      <FormControl>
        {showFilter && (
          <Grid sx={{ mb: 3 }}>
            <CustomToggleSwitch
              label="Forecasts"
              title="Enable/Disable the forecast filter"
              onChange={handleForecastFilterChange}
              checked={(enableForecastFilter && summaryForecastFilter) ?? false}
              disabled={!enableForecastFilter}
              className="forecast-toggle"
            />
            {!isHotelForecast && (
              <Typography sx={{ fontSize: 11.5, color: 'gray' }}>{ForecastDisabledText}</Typography>
            )}
          </Grid>
        )}

        <Grid container alignItems="center" className='booking-pace-horizon'>
          <FormLabel>Booking Pace Horizon</FormLabel>
          <Tooltip
            color="primary"
            className={styles.infoIcon}
            title="Not settable when extended forecasts are displayed."
          >
            <InfoIcon />
          </Tooltip>
        </Grid>
        <RadioGroup value={paceHorizon ?? null} onChange={handlePaceHorizonChange}>
          {paceList
            ?.sort(
              (element1, element2) =>
                element1.label?.[0] &&
                element2.label?.[0] &&
                Number(element1?.label?.[0]?.split(' ')[0]) -
                  Number(element2?.label?.[0]?.split(' ')[0])
            )
            ?.map((item) => (
              <FormControlLabel
                key={item?.id}
                value={item?.id}
                control={<Radio />}
                label={item?.label}
                sx={{
                  my: -1,
                }}
                disabled={!isPlot && visualToggle}
              />
            ))}
        </RadioGroup>
      </FormControl>
      {!isPlot && (
        <FormControl className='booking-pace-parameter'>
          <FormLabel sx={{ paddingBottom: 1 }}>Booking Pace Parameter</FormLabel>
          <ToggleButtonGroup
            fullWidth
            color="primary"
            value={paceVariable}
            exclusive
            onChange={handlePaceVariableChange}
          >
            {paceVariableList?.map((item) => (
              <ToggleButton key={item?.id} value={item?.label}>
                {item?.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </FormControl>
      )}
    </CustomStack>
  );
};
//
export default BookingPaceWindow;
